import { AddFilterButton, Filters } from '@/components'
import {
  Card,
  CardContent,
  CardHeading,
  ChartFilters,
  FigureLabel,
  FigureValue,
} from '@/components/ui'
import {
  applyFilters,
  type FieldSchema,
  FiltersProvider,
  useFilters,
} from '@/stores'
import {
  chartColors,
  chartPalette,
  cn,
  numberToCompactCurrency,
  numberToCurrency,
  percentFromRate,
} from '@/utils'
import { useSecuritiesReadState } from '../../hooks/project'
import { formatAssetClass } from '../../labels'
import { SecuritiesFailedState } from '../portfolio/SecuritiesFailedState'
import { SecuritiesPendingState } from '../portfolio/SecuritiesPendingState'
import type { PortfolioAccountSummary } from '../portfolio/types'
import {
  sumOrNull,
  usePortfolioDashboard,
} from '../portfolio/usePortfolioDashboard'
import { ValueTrendChart } from '../portfolio/ValueTrendChart'
import { OverviewBarRow } from './OverviewBarRow'
import { OverviewViewLink } from './OverviewViewLink'

interface OverviewPortfolioProps {
  slug: string
}

// How many asset classes to name before the rest fold into "Other".
const MAX_CLASSES = 5

const PORTFOLIO_FILTER_CONTEXT_ID = 'overview-portfolio'

// Same account filter the cash sections use, so the portfolio narrows the same way.
const ACCOUNT_FILTER_SCHEMA: FieldSchema[] = [
  { field: 'accountNumber', label: 'Account', type: 'select', multiple: true },
]

const sumOf = <Item,>(items: Item[], pick: (item: Item) => number): number =>
  items.reduce((running, item) => running + pick(item), 0)

// Distinct brokerage accounts as filter options, labeled like the account-activity filter.
const buildAccountValues = (accounts: PortfolioAccountSummary[]) =>
  accounts
    .slice()
    .sort((first, second) =>
      first.accountNumber.localeCompare(second.accountNumber),
    )
    .map((account) => ({
      value: account.accountNumber,
      label: account.accountNumber,
      description: `${account.institution} · ${account.accountType} · ${
        account.holdingsCount
      } ${account.holdingsCount === 1 ? 'holding' : 'holdings'}`,
    }))

// The brokerage side of the case at a glance. Self-hiding: a case with no securities statements
// renders nothing. With portfolios it shows market value, unrealized gain/loss, allocation, and trend.
export const OverviewPortfolio = ({ slug }: OverviewPortfolioProps) => {
  const { accounts } = usePortfolioDashboard()
  const securitiesReadState = useSecuritiesReadState()

  // No holdings yet: say the brokerage statements are being read or failed to read. Otherwise hide.
  if (accounts.length === 0) {
    if (securitiesReadState !== 'reading' && securitiesReadState !== 'failed')
      return null
    return (
      <Card>
        <CardHeading title="Portfolio" />
        <CardContent>
          {securitiesReadState === 'reading' ? (
            <SecuritiesPendingState
              layout="card"
              detail="The system is verifying holdings and value, usually within 24 hours. They appear here when it's done."
            />
          ) : (
            <SecuritiesFailedState layout="card" />
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <FiltersProvider contextId={PORTFOLIO_FILTER_CONTEXT_ID}>
      <OverviewPortfolioContent slug={slug} />
    </FiltersProvider>
  )
}

const OverviewPortfolioContent = ({ slug }: OverviewPortfolioProps) => {
  const { accounts, holdings, periods } = usePortfolioDashboard()
  const filters = useFilters()

  const filteredAccounts = applyFilters(
    accounts,
    filters,
    ACCOUNT_FILTER_SCHEMA,
  )
  const filteredHoldings = applyFilters(
    holdings,
    filters,
    ACCOUNT_FILTER_SCHEMA,
  )

  const marketValue = sumOf(filteredAccounts, (account) => account.marketValue)
  const costBasis = sumOrNull(
    filteredAccounts.map((account) => account.costBasis),
  )
  const unrealized = sumOrNull(
    filteredAccounts.map((account) => account.unrealized),
  )
  const unrealizedPercent =
    costBasis && unrealized != null ? (unrealized / costBasis) * 100 : null
  const reconciledCount = filteredAccounts.filter(
    (account) => account.validated,
  ).length

  // Allocation reads each account's latest snapshot so the same shares aren't counted once per month.
  const latestByAccount = new Map(
    filteredAccounts.map((account) => [account.id, account.latestPeriodKey]),
  )
  const snapshotHoldings = filteredHoldings.filter(
    (holding) => holding.period === latestByAccount.get(holding.account),
  )

  const valueByClass = new Map<string, number>()
  for (const holding of snapshotHoldings) {
    const label = holding.assetClass || 'Unclassified'
    valueByClass.set(
      label,
      (valueByClass.get(label) ?? 0) + holding.marketValue,
    )
  }
  const classes = Array.from(valueByClass.entries())
    .map(([label, value]) => ({ label, value }))
    .filter((slice) => slice.value > 0)
    .sort((first, second) => second.value - first.value)
  const namedClasses = classes.slice(0, MAX_CLASSES)
  const otherValue = sumOf(classes.slice(MAX_CLASSES), (slice) => slice.value)
  const allocationTotal = marketValue || 1

  // Total value at each statement period, oldest first, for the trend line. Accounts sharing a month
  // collapse onto one point so the line reads as one portfolio.
  const periodLabelByKey = new Map(
    periods.map((period) => [period.key, period.label]),
  )
  const valueByPeriod = new Map<string, number>()
  for (const holding of filteredHoldings) {
    valueByPeriod.set(
      holding.period,
      (valueByPeriod.get(holding.period) ?? 0) + holding.marketValue,
    )
  }
  const trendPoints = Array.from(valueByPeriod.entries())
    .sort(([first], [second]) => first.localeCompare(second))
    .map(([key, value]) => ({
      key,
      label: periodLabelByKey.get(key) ?? key,
      value,
    }))

  const figures = [
    {
      label: 'Cost basis',
      value: costBasis == null ? '—' : numberToCompactCurrency(costBasis),
    },
    { label: 'Accounts', value: String(filteredAccounts.length) },
    {
      label: 'Reconciled',
      value: `${reconciledCount} of ${filteredAccounts.length}`,
    },
  ]

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Filters
          dialogId={PORTFOLIO_FILTER_CONTEXT_ID}
          schema={ACCOUNT_FILTER_SCHEMA}
          getUniqueValues={() => buildAccountValues(accounts)}
        />
        <ChartFilters>
          <AddFilterButton
            dialogId={PORTFOLIO_FILTER_CONTEXT_ID}
            schema={ACCOUNT_FILTER_SCHEMA}
          />
        </ChartFilters>
      </div>

      <Card>
        <CardHeading
          title="Portfolio"
          actions={<OverviewViewLink to={`/projects/${slug}/portfolio`} />}
        />
        <CardContent>
          {filteredAccounts.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No accounts match this filter.
            </p>
          ) : (
            <>
              <div className="@container">
                <div className="grid grid-cols-1 gap-6 @2xl:grid-cols-2">
                  <div>
                    <FigureValue className="text-3xl">
                      {numberToCurrency(marketValue)}
                    </FigureValue>
                    <div className="mt-1.5 text-sm">
                      {unrealized == null ? (
                        <span className="font-medium text-blue-5">—</span>
                      ) : (
                        <span
                          className={cn(
                            'font-medium tabular-nums',
                            unrealized > 0 && 'text-emerald-600',
                            unrealized < 0 && 'text-red-600',
                            unrealized === 0 && 'text-blue-5',
                          )}
                        >
                          {unrealized >= 0 ? '+' : '-'}
                          {numberToCurrency(Math.abs(unrealized))}
                          {unrealizedPercent != null &&
                            ` (${unrealized >= 0 ? '+' : '-'}${Math.abs(
                              unrealizedPercent,
                            ).toFixed(1)}%)`}
                        </span>
                      )}{' '}
                      <span className="text-muted-foreground">unrealized</span>
                    </div>

                    <div className="mt-4 flex flex-wrap divide-x divide-border">
                      {figures.map((figure) => (
                        <div
                          key={figure.label}
                          className="px-6 first:pl-0 last:pr-0"
                        >
                          <FigureLabel>{figure.label}</FigureLabel>
                          <FigureValue className="mt-0.5 text-lg">
                            {figure.value}
                          </FigureValue>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <FigureLabel className="mb-2">Allocation</FigureLabel>
                    <div className="space-y-1">
                      {namedClasses.map((slice, index) => (
                        <OverviewBarRow
                          key={slice.label}
                          label={formatAssetClass(slice.label)}
                          color={chartPalette[index % chartPalette.length]}
                          fraction={slice.value / allocationTotal}
                          value={numberToCompactCurrency(slice.value)}
                          valueMuted={percentFromRate(
                            slice.value / allocationTotal,
                          )}
                        />
                      ))}
                      {otherValue > 0 ? (
                        <OverviewBarRow
                          label="Other"
                          color={chartColors.gray}
                          fraction={otherValue / allocationTotal}
                          value={numberToCompactCurrency(otherValue)}
                          valueMuted={percentFromRate(
                            otherValue / allocationTotal,
                          )}
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              {trendPoints.length >= 2 ? (
                <div className="mt-6 border-t border-border pt-4">
                  <FigureLabel className="mb-2">Value over time</FigureLabel>
                  <ValueTrendChart points={trendPoints} />
                </div>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
